/* eslint-disable react/no-array-index-key */
import { Button } from '@efexteam/components';
import useStakeholderFileService from '@efex-components/EfexDS/FileInput/useStakeholderFileService';
import Autorenew from '@mui/icons-material/Autorenew';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { selectBusiness } from '@pages/profile/store/selectors';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import clientSvc from '../../../../../services/client/clientSvc';
import {
  ICreateStakeholderV2,
  StakeHoldersFormData,
  UploadStaStakeHolders,
} from '../../../../../services/client/types';
import { useFormData } from '../../context';
import StakeHolderFormUSA from './components/StakeHolderContainer';
import StakeHolderProvider from './context/StakeHolderProvider';

interface IStakeHoldersForm {
  isDisabledEdit?: boolean;
  loading?: boolean;
  nextFormStep: (data: any) => void;
  prevStep: () => void;
}

const StakeHolders = ({
  isDisabledEdit = false,
  loading: loadingOnboarding,
  nextFormStep,
  prevStep,
}: IStakeHoldersForm) => {
  const { t } = useTranslation();
  const company = useSelector(selectBusiness);

  const [stakeHoldersConfirmed, setStakeHoldersConfirmed] =
    useState<boolean>(false);
  const { data, stkPartials, getStakeHolder } = useFormData();

  const { handleDropChangeFile } = useStakeholderFileService({
    getStakeHolder,
  });

  const { loading, error } = useApiCall(
    ({ enterpriseID, values }: ICreateStakeholderV2) =>
      clientSvc.createStakeHolderV2({ enterpriseID, values })
  );

  const { loading: loadingUpdate, error: errorUpdate } = useApiCall(
    (values: UploadStaStakeHolders) =>
      clientSvc.updateStakeHolder(values, company?.enterpriseId ?? '')
  );
  const {
    loading: loadingDelete,
    error: errorDelete,
    data: deleteData,
    invoke: deleteStakeHolder,
  } = useApiCall((id: string | number) => clientSvc.deleteStakeHolder(id));

  const [stakeHolders, setStakeHolders] = useState<StakeHoldersFormData[]>([]);

  const onSubmit = async () => {
    nextFormStep({
      ...data,
      isLastStep: true,
      isStakeHolderDataConfirm:
        data?.countryOrigin === 'USA' ? stakeHoldersConfirmed : undefined,
    });
  };

  const handleBack = () => {
    prevStep();
  };

  const addNewStakeHolder = () => {
    const newData = [...stakeHolders, {} as StakeHoldersFormData];
    setStakeHolders(newData);
  };

  const removeStakeHolder = async (index: number) => {
    const isExisting = (stkPartials as StakeHoldersFormData[]).find(
      (record) => record.id === index
    );
    if (isExisting) {
      await deleteStakeHolder(index);
      return;
    }
    const newData = [...stakeHolders];
    newData.splice(index, 1);
    setStakeHolders(newData);
  };

  useEffect(() => {
    if (deleteData) {
      getStakeHolder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteData]);

  useEffect(() => {
    if (stkPartials) {
      setStakeHolders([]);
      setTimeout(() => {
        setStakeHolders(stkPartials);
      }, 100);
    }
  }, [stkPartials]);

  const renderForms = () =>
    stakeHolders.map((form, index) => {
      const formError = '';

      return (
        <div className="max-w-[1000px] mx-auto" key={index}>
          <StakeHolderProvider>
            <StakeHolderFormUSA
              index={index}
              getStakeHolder={getStakeHolder}
              formError={formError}
              remove={removeStakeHolder}
              handleDropChangeFile={handleDropChangeFile}
              data={form}
              loading={loading || loadingUpdate || loadingDelete}
              stakeholderId={form.id}
            />
          </StakeHolderProvider>
        </div>
      );
    });

  return (
    <Grid container>
      <Grid item xs={12}>
        <div className="max-w-[1000px] mx-auto">
          <Typography variant="h4" className="text-left !font-medium">
            {t('onbStakeHoldersTitle')}
          </Typography>
          {!isDisabledEdit ? (
            <Button
              onClick={addNewStakeHolder}
              appearance="secondary"
              className="ml-auto mb-3 mt-5"
              endIcon={<PersonAddAltIcon />}
              disabled={stakeHolders.length >= 4}
            >
              {t('onbAdd')}
            </Button>
          ) : null}
          <Alert severity="info">{t('infoStakeholders')}</Alert>
          {error || errorUpdate || errorDelete ? (
            <Alert className="!my-5" severity="error">
              {t('onbStkCreateError')}
            </Alert>
          ) : null}
          {renderForms()}
        </div>
      </Grid>

      <Grid item xs={12} className="!my-5 !px-4">
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={stakeHoldersConfirmed}
                onChange={(e) => setStakeHoldersConfirmed(e.target.checked)}
              />
            }
            label={t('onbStakeCheck')}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <div className="flex justify-around !mt-5 max-w-[1000px] mx-auto">
          {!isDisabledEdit ? (
            <>
              <Button
                size="large"
                className="mt-2 text-[14px]"
                type="button"
                appearance="secondary"
                onClick={handleBack}
              >
                {t('backBtn')}
              </Button>
              {stakeHolders &&
                stakeHolders[0] &&
                stakeHolders[0].name &&
                stakeHolders[0].name?.length > 0 && (
                  <Button
                    appearance="primary"
                    size="large"
                    className="mt-2 text-[14px]"
                    type="button"
                    onClick={onSubmit}
                    disabled={
                      !(
                        stakeHolders &&
                        stakeHolders[0] &&
                        stakeHolders[0].name &&
                        stakeHolders[0].name?.length > 0
                      ) ||
                      (data?.countryOrigin === 'USA' &&
                        !stakeHoldersConfirmed) ||
                      loadingOnboarding
                    }
                    endIcon={
                      !loadingOnboarding ? undefined : (
                        <Autorenew className="animate-spin" />
                      )
                    }
                  >
                    {t('continue')}
                  </Button>
                )}
            </>
          ) : (
            <Alert severity="info">{t('kybStepTwoTitle')}</Alert>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default StakeHolders;
